import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { ApiError, saveDocumentWithKeepalive } from '../api/client';
import type { EntityId } from '../api/entityId';
import {
  CommandsService,
  PageBootstrapKind,
  QueriesService,
  SaveDocumentCommandStatus,
  type CreateDocumentCommandResult,
  type CreateFolderCommandResult,
  type CreateProjectCommandResult,
  type DocumentTreeResponse,
  type FolderTreeResponse,
  type GetDocumentQueryResponse,
  type GetPageBootstrapQueryResponse,
  type GetWorkspaceQueryResponse,
  type ProjectTreeResponse
} from '../api/generated';
import { getErrorMessage, useSessionStore } from './session';
import {
  getEditorTabsState,
  getWorkspaceTreeExpansionState,
  recordRecentDocument,
  setEditorTabsState,
  setWorkspaceTreeExpansionState,
  type EditorPaneState,
  type EditorTabsState,
  type EditorTabState,
  type WorkspaceTreeExpansionState
} from './workspaceViewport';
import {
  deleteDocumentDraft,
  getDocumentDraft,
  setDocumentDraft,
  type DocumentDraftRecord
} from './documentDrafts';
import {
  reportWorkspaceDocumentChanged,
  subscribeWorkspaceRealtime,
  syncWorkspaceRealtimeOpenDocuments,
  type DocumentCommentCreatedMessage,
  type DocumentContentSavedMessage,
  type DocumentPresenceChangedMessage,
  type DocumentPresenceMode,
  type DocumentPresenceSnapshotMessage,
  type WorkspaceTreeChangedMessage
} from './workspaceRealtime';

export interface WorkspaceDocumentTree {
  id: EntityId;
  projectId: EntityId;
  folderId: EntityId | null;
  title: string;
  contentVersion: number;
}

export interface WorkspaceFolderTree {
  id: EntityId;
  projectId: EntityId;
  parentFolderId: EntityId | null;
  name: string;
  isExpanded: boolean;
  folders: WorkspaceFolderTree[];
  documents: WorkspaceDocumentTree[];
}

export interface WorkspaceProjectTree {
  id: EntityId;
  name: string;
  canEdit: boolean;
  isExpanded: boolean;
  folders: WorkspaceFolderTree[];
  documents: WorkspaceDocumentTree[];
}

interface WorkspaceResponse {
  projects: WorkspaceProjectTree[];
}

export type DocumentSaveStateKind =
  | 'saved'
  | 'unsaved'
  | 'saving'
  | 'retrying'
  | 'conflict'
  | 'blocked';

export interface DocumentSaveState {
  kind: DocumentSaveStateKind;
  message: string | null;
}

export type WorkspaceRefreshSafety = 'conflict' | 'blocked' | 'retrying' | 'pending' | 'safe';

export interface DocumentSaveConflict {
  documentId: EntityId;
  title: string;
  serverContentMarkdown: string;
  serverContentVersion: number;
  serverSavedAt: GetDocumentQueryResponse['savedAt'];
  serverLastModifiedByUserName: string;
}

export interface DocumentPresenceParticipant {
  userId: EntityId;
  userName: string;
  mode: DocumentPresenceMode;
  lastChangedAt: string | null;
}

interface WorkspaceState {
  workspace: WorkspaceResponse | null;
  editorState: EditorTabsState;
  documents: Record<EntityId, GetDocumentQueryResponse>;
  drafts: Record<EntityId, string>;
  draftBaseContentVersions: Record<EntityId, number>;
  dirtyDocuments: Record<EntityId, boolean>;
  saveTimers: Record<EntityId, number>;
  saveStates: Record<EntityId, DocumentSaveState>;
  retryAttempts: Record<EntityId, number>;
  pendingDraftWrites: Record<EntityId, number>;
  saveConflicts: Record<EntityId, DocumentSaveConflict>;
  documentPresence: Record<EntityId, DocumentPresenceParticipant[]>;
  documentCommentRevisions: Record<EntityId, number>;
  documentCommentsRecoveryRevision: number;
  error: string | null;
  unloadRegistered: boolean;
}

interface WorkspaceMutationSnapshot {
  workspace: WorkspaceResponse | null;
  editorState: EditorTabsState;
  documents: Record<EntityId, GetDocumentQueryResponse>;
  drafts: Record<EntityId, string>;
  draftBaseContentVersions: Record<EntityId, number>;
  dirtyDocuments: Record<EntityId, boolean>;
  saveStates: Record<EntityId, DocumentSaveState>;
  retryAttempts: Record<EntityId, number>;
  pendingDraftWrites: Record<EntityId, number>;
  saveConflicts: Record<EntityId, DocumentSaveConflict>;
  documentPresence: Record<EntityId, DocumentPresenceParticipant[]>;
}

const autosaveDelayMs = 10000;
const retryDelayMs = [2000, 5000, 15000, 30000, 60000];
const primaryPaneIndex = 0;
const secondaryPaneIndex = 1;
let workspaceLoadPromise: Promise<void> | null = null;
const draftWriteQueue: Record<string, Promise<void>> = {};

export const useWorkspaceStore = defineStore('workspace', {
  state: (): WorkspaceState => ({
    workspace: null,
    editorState: createEmptyEditorState(),
    documents: {},
    drafts: {},
    draftBaseContentVersions: {},
    dirtyDocuments: {},
    saveTimers: {},
    saveStates: {},
    retryAttempts: {},
    pendingDraftWrites: {},
    saveConflicts: {},
    documentPresence: {},
    documentCommentRevisions: {},
    documentCommentsRecoveryRevision: 0,
    error: null,
    unloadRegistered: false
  }),
  getters: {
    activeDocumentId: state => {
      const pane = state.editorState.panes.find(candidate => candidate.paneIndex === state.editorState.activePaneIndex);
      return pane?.activeDocumentId ?? null;
    },
    visiblePanes: state => {
      const panes = [...state.editorState.panes].sort((left, right) => left.paneIndex - right.paneIndex);
      const panesWithTabs = panes.filter(pane => pane.tabs.length > 0);
      return panesWithTabs.length > 0 ? panesWithTabs : panes.filter(pane => pane.paneIndex === primaryPaneIndex);
    },
    activeSaveConflict: state => {
      return Object.values(state.saveConflicts)[0] ?? null;
    },
    refreshSafety: (state): WorkspaceRefreshSafety => {
      const saveKinds = Object.values(state.saveStates).map(saveState => saveState.kind);
      if (Object.keys(state.saveConflicts).length > 0 || saveKinds.includes('conflict')) {
        return 'conflict';
      }

      if (saveKinds.includes('blocked')) {
        return 'blocked';
      }

      if (saveKinds.includes('retrying')) {
        return 'retrying';
      }

      if (Object.values(state.dirtyDocuments).some(Boolean)
        || Object.values(state.pendingDraftWrites).some(count => count > 0)
        || saveKinds.includes('unsaved')
        || saveKinds.includes('saving')) {
        return 'pending';
      }

      return 'safe';
    },
    getDocumentCommentsRevision: state => (documentId: EntityId): number => {
      return state.documentCommentRevisions[documentId] ?? 0;
    }
  },
  actions: {
    async loadWorkspace(): Promise<void> {
      if (workspaceLoadPromise) {
        await workspaceLoadPromise;
        return;
      }

      workspaceLoadPromise = this.runLoadWorkspace();
      try {
        await workspaceLoadPromise;
      } finally {
        workspaceLoadPromise = null;
      }
    },
    async runLoadWorkspace(): Promise<void> {
      this.workspace = normalizeWorkspaceResponse(await QueriesService.queryGetWorkspaceQuery({ requestBody: {} }));
      this.applyTreeExpansionState();
      this.restoreEditorState();
      this.syncEditorStateWithWorkspace();
      this.error = null;
      this.registerUnloadSave();
      await this.loadActiveDocuments();
      await this.refreshRealtimeWorkspaceSubscription();
    },
    async reloadWorkspace(): Promise<void> {
      const response = await QueriesService.queryGetPageBootstrapQuery({
        requestBody: {
          pageKind: PageBootstrapKind.WORKSPACE,
          workspaceDocumentIds: getOpenDocumentIds(this.editorState)
        }
      });
      await this.applyBootstrap(response);
    },
    async loadActiveDocuments(): Promise<void> {
      if (!this.workspace) {
        return;
      }

      for (const pane of this.editorState.panes) {
        if (pane.activeDocumentId != null) {
          await this.loadDocument(pane.activeDocumentId);
        }
      }
    },
    async loadDocument(documentId: EntityId, force = false): Promise<void> {
      if (!force && this.documents[documentId]) {
        return;
      }

      const document = await QueriesService.queryGetDocumentQuery({ requestBody: { id: documentId } });
      await this.applyLoadedDocument(document);
    },
    async applyLoadedDocument(document: GetDocumentQueryResponse): Promise<void> {
      const documentId = toEntityId(document.id);
      this.documents[documentId] = document;
      const contentVersion = toContentVersion(document.contentVersion);
      let localDraft: DocumentDraftRecord | null = null;
      try {
        localDraft = await getDocumentDraft(this.requireUserId(), documentId);
      } catch (error) {
        this.error = getErrorMessage(error, 'Unable to load local document draft.');
      }

      if (localDraft != null && localDraft.contentMarkdown !== document.contentMarkdown) {
        this.drafts[documentId] = localDraft.contentMarkdown;
        this.draftBaseContentVersions[documentId] = localDraft.baseContentVersion;
        this.dirtyDocuments[documentId] = true;
        this.setSaveState(documentId, 'unsaved', null);
        this.scheduleAutosave(documentId);
      } else {
        this.drafts[documentId] = document.contentMarkdown;
        this.draftBaseContentVersions[documentId] = contentVersion;
        delete this.dirtyDocuments[documentId];
        delete this.saveStates[documentId];
        delete this.saveConflicts[documentId];
        if (localDraft != null) {
          this.queueDraftDelete(documentId);
        }
      }

      if (localDraft != null || this.error == null) {
        this.error = null;
      }
    },
    async applyBootstrap(response: GetPageBootstrapQueryResponse): Promise<void> {
      if (response.workspace == null) {
        return;
      }

      this.workspace = normalizeWorkspaceResponse(response.workspace);
      this.applyTreeExpansionState();
      this.restoreEditorState();
      this.syncEditorStateWithWorkspace();
      this.error = null;
      this.registerUnloadSave();
      for (const document of response.workspaceDocuments ?? []) {
        await this.applyLoadedDocument(document);
      }

      await this.refreshRealtimeWorkspaceSubscription();
    },
    async openDocument(documentId: EntityId, paneIndex: number | null = null): Promise<void> {
      this.ensureWorkspaceLoaded();
      const metadata = this.getDocumentTabMetadata(documentId);
      const targetPaneIndex = paneIndex ?? this.findPaneContainingDocument(documentId)?.paneIndex ?? this.editorState.activePaneIndex;
      const pane = this.getPane(targetPaneIndex);
      let tab = pane.tabs.find(candidate => candidate.documentId === documentId);
      if (tab == null) {
        tab = {
          ...metadata,
          paneIndex: pane.paneIndex,
          sortOrder: getNextSortOrder(pane)
        };
        pane.tabs.push(tab);
        sortTabs(pane.tabs);
      } else {
        Object.assign(tab, metadata);
      }

      pane.activeDocumentId = documentId;
      this.editorState.activePaneIndex = pane.paneIndex;
      this.persistEditorState();
      this.revealDocument(documentId);
      const userId = useSessionStore().profile?.id;
      if (userId != null) {
        recordRecentDocument(userId, documentId);
      }

      await this.loadDocument(documentId);
      void this.syncRealtimeOpenDocuments();
    },
    revealDocument(documentId: EntityId): void {
      for (const project of this.workspace?.projects ?? []) {
        const document = findDocumentInProject(project, documentId);
        if (document == null) {
          continue;
        }

        project.isExpanded = true;
        let folderId = document.folderId;
        while (folderId != null) {
          const folder = this.findFolder(folderId);
          if (folder == null) {
            break;
          }

          folder.isExpanded = true;
          folderId = folder.parentFolderId;
        }

        this.persistTreeExpansionState();
        return;
      }
    },
    async openDocumentToSide(document: WorkspaceDocumentTree): Promise<void> {
      await this.openDocument(document.id, secondaryPaneIndex);
    },
    async setActivePane(paneIndex: number): Promise<void> {
      this.editorState.activePaneIndex = normalizePaneIndex(paneIndex);
      this.persistEditorState();
    },
    async activateDocument(paneIndex: number, documentId: EntityId): Promise<void> {
      const pane = this.getPane(paneIndex);
      if (pane.tabs.every(tab => tab.documentId !== documentId)) {
        throw new Error('Document must be opened in the pane before activation.');
      }

      await this.loadDocument(documentId);
      pane.activeDocumentId = documentId;
      this.editorState.activePaneIndex = pane.paneIndex;
      this.persistEditorState();
      const userId = useSessionStore().profile?.id;
      if (userId != null) {
        recordRecentDocument(userId, documentId);
      }
    },
    setDraft(documentId: EntityId, contentMarkdown: string): void {
      this.drafts[documentId] = contentMarkdown;
      const document = this.documents[documentId];
      if (!document) {
        throw new Error('Document must be loaded before changing draft content.');
      }

      if (document && contentMarkdown === document.contentMarkdown) {
        delete this.dirtyDocuments[documentId];
        delete this.saveConflicts[documentId];
        delete this.saveStates[documentId];
        this.clearSaveTimer(documentId);
        this.queueDraftDelete(documentId);
        return;
      }

      if (this.draftBaseContentVersions[documentId] == null) {
        this.draftBaseContentVersions[documentId] = toContentVersion(document.contentVersion);
      }

      this.dirtyDocuments[documentId] = true;
      reportWorkspaceDocumentChanged(documentId);
      if (this.saveConflicts[documentId]) {
        this.setSaveState(documentId, 'conflict', null);
        this.queueDraftWrite(documentId, contentMarkdown, this.draftBaseContentVersions[documentId]);
        return;
      }

      this.retryAttempts[documentId] = 0;
      this.setSaveState(documentId, 'unsaved', null);
      this.queueDraftWrite(documentId, contentMarkdown, this.draftBaseContentVersions[documentId]);
      this.scheduleAutosave(documentId);
    },
    scheduleAutosave(documentId: EntityId): void {
      this.clearSaveTimer(documentId);
      if (this.saveConflicts[documentId]) {
        return;
      }

      this.saveTimers[documentId] = window.setTimeout(() => {
        void this.saveDocument(documentId);
      }, autosaveDelayMs);
    },
    clearSaveTimer(documentId: EntityId): void {
      const existing = this.saveTimers[documentId];
      if (existing != null) {
        window.clearTimeout(existing);
        delete this.saveTimers[documentId];
      }
    },
    async saveDocument(documentId: EntityId): Promise<boolean> {
      const document = this.documents[documentId];
      if (!document || !this.dirtyDocuments[documentId]) {
        return true;
      }

      if (this.saveConflicts[documentId]) {
        return false;
      }

      const draft = this.getDraft(documentId);
      if (draft === document.contentMarkdown) {
        delete this.dirtyDocuments[documentId];
        delete this.saveStates[documentId];
        this.clearSaveTimer(documentId);
        await this.deleteDraftFromStorage(documentId);
        return true;
      }

      try {
        this.setSaveState(documentId, 'saving', null);
        const expectedContentVersion = this.draftBaseContentVersions[documentId] ?? toContentVersion(document.contentVersion);
        const result = await CommandsService.commandSaveDocumentCommand({
          requestBody: {
            documentId,
            expectedContentVersion,
            contentMarkdown: draft
          }
        });
        if (result.status === SaveDocumentCommandStatus.CONFLICT) {
          if (result.conflictDocument == null) {
            throw new Error('Save conflict response did not include the server document.');
          }

          await this.registerSaveConflict(documentId, result.conflictDocument);
          return false;
        }

        const contentVersion = toContentVersion(result.contentVersion);
        document.contentVersion = contentVersion;
        document.savedAt = result.savedAt;
        document.contentMarkdown = draft;
        const profile = useSessionStore().profile;
        if (profile != null) {
          document.lastModifiedByUserId = profile.id;
          document.lastModifiedByUserName = profile.name;
        }

        this.updateDocumentContentVersion(documentId, contentVersion);
        if (this.getDraft(documentId) === draft) {
          delete this.dirtyDocuments[documentId];
          this.draftBaseContentVersions[documentId] = contentVersion;
          delete this.retryAttempts[documentId];
          delete this.saveStates[documentId];
          this.clearSaveTimer(documentId);
          await this.deleteDraftFromStorage(documentId);
        } else {
          this.draftBaseContentVersions[documentId] = contentVersion;
          this.queueDraftWrite(documentId, this.getDraft(documentId), contentVersion);
          this.scheduleAutosave(documentId);
        }

        this.error = null;
        return true;
      } catch (error) {
        this.error = getErrorMessage(error, 'Unable to save document.');
        const failure = classifySaveFailure(error);
        if (failure === 'transient') {
          this.scheduleRetry(documentId);
        } else {
          this.clearSaveTimer(documentId);
          this.setSaveState(documentId, 'blocked', this.error);
        }

        return false;
      }
    },
    async closeDocument(paneIndex: number, documentId: EntityId): Promise<void> {
      if (!await this.saveDocument(documentId)) {
        return;
      }

      const pane = this.getPane(paneIndex);
      pane.tabs = pane.tabs.filter(tab => tab.documentId !== documentId);
      if (pane.activeDocumentId === documentId) {
        pane.activeDocumentId = pane.tabs[0]?.documentId ?? null;
      }

      if (this.editorState.activePaneIndex === pane.paneIndex && pane.tabs.length === 0) {
        this.editorState.activePaneIndex = this.getPane(secondaryPaneIndex).tabs.length > 0 ? secondaryPaneIndex : primaryPaneIndex;
      }

      this.persistEditorState();
      if (pane.activeDocumentId != null) {
        await this.loadDocument(pane.activeDocumentId);
      }

      this.purgeClosedDocuments();
      void this.syncRealtimeOpenDocuments();
    },
    async retrySaveDocument(documentId: EntityId): Promise<void> {
      delete this.retryAttempts[documentId];
      this.clearSaveTimer(documentId);
      await this.saveDocument(documentId);
    },
    async overwriteSaveConflict(documentId: EntityId): Promise<void> {
      const conflict = this.saveConflicts[documentId];
      if (conflict == null) {
        throw new Error('Document save conflict was not found.');
      }

      this.draftBaseContentVersions[documentId] = conflict.serverContentVersion;
      delete this.saveConflicts[documentId];
      this.dirtyDocuments[documentId] = true;
      this.queueDraftWrite(documentId, this.getDraft(documentId), conflict.serverContentVersion);
      await this.saveDocument(documentId);
    },
    async rollbackSaveConflict(documentId: EntityId): Promise<void> {
      const conflict = this.saveConflicts[documentId];
      const document = this.documents[documentId];
      if (conflict == null || document == null) {
        throw new Error('Document save conflict was not found.');
      }

      document.contentMarkdown = conflict.serverContentMarkdown;
      document.contentVersion = conflict.serverContentVersion;
      document.savedAt = conflict.serverSavedAt;
      this.drafts[documentId] = conflict.serverContentMarkdown;
      this.draftBaseContentVersions[documentId] = conflict.serverContentVersion;
      this.updateDocumentContentVersion(documentId, conflict.serverContentVersion);
      delete this.dirtyDocuments[documentId];
      delete this.saveConflicts[documentId];
      delete this.saveStates[documentId];
      delete this.retryAttempts[documentId];
      this.clearSaveTimer(documentId);
      await this.deleteDraftFromStorage(documentId);
      this.error = null;
    },
    async discardLocalDraft(documentId: EntityId): Promise<void> {
      this.clearSaveTimer(documentId);
      delete this.dirtyDocuments[documentId];
      delete this.saveConflicts[documentId];
      delete this.saveStates[documentId];
      delete this.retryAttempts[documentId];
      delete this.draftBaseContentVersions[documentId];
      delete this.drafts[documentId];
      await this.deleteDraftFromStorage(documentId);
    },
    getDraft(documentId: EntityId): string {
      return this.drafts[documentId] ?? '';
    },
    getSaveState(documentId: EntityId): string {
      const state = this.saveStates[documentId];
      if (state?.kind === 'saving') {
        return 'Saving';
      }

      if (state?.kind === 'retrying') {
        return 'Offline/server unavailable';
      }

      if (state?.kind === 'conflict') {
        return 'Save conflict';
      }

      if (state?.kind === 'blocked') {
        return 'Save blocked';
      }

      if (state?.kind === 'unsaved' || this.dirtyDocuments[documentId]) {
        return 'Unsaved changes';
      }

      const document = this.documents[documentId];
      return document ? `Saved at ${formatDateTime(document.savedAt)}` : 'Saved';
    },
    getSaveStateClass(documentId: EntityId): string {
      const state = this.saveStates[documentId];
      if (state?.kind === 'saving') {
        return 'saving';
      }

      if (state?.kind === 'retrying') {
        return 'retrying';
      }

      if (state?.kind === 'conflict') {
        return 'conflict';
      }

      if (state?.kind === 'blocked') {
        return 'blocked';
      }

      return this.dirtyDocuments[documentId] ? 'dirty' : 'saved';
    },
    canRetrySave(documentId: EntityId): boolean {
      const state = this.saveStates[documentId];
      return this.dirtyDocuments[documentId] === true
        && (state?.kind === 'retrying' || state?.kind === 'blocked');
    },
    resetRealtimeState(): void {
      this.documentPresence = {};
      this.documentCommentRevisions = {};
      this.documentCommentsRecoveryRevision = 0;
    },
    async refreshRealtimeWorkspaceSubscription(): Promise<void> {
      try {
        await subscribeWorkspaceRealtime();
        await syncWorkspaceRealtimeOpenDocuments(getOpenDocumentIds(this.editorState));
      } catch (error) {
        this.error = getErrorMessage(error, 'Unable to refresh realtime workspace updates.');
      }
    },
    async syncRealtimeOpenDocuments(): Promise<void> {
      try {
        await syncWorkspaceRealtimeOpenDocuments(getOpenDocumentIds(this.editorState));
      } catch (error) {
        this.error = getErrorMessage(error, 'Unable to sync realtime document presence.');
      }
    },
    getRealtimeOpenDocumentIds(): EntityId[] {
      return getOpenDocumentIds(this.editorState);
    },
    applyDocumentCommentCreated(message: DocumentCommentCreatedMessage): void {
      const documentId = toEntityId(message.documentId);
      this.documentCommentRevisions[documentId] = (this.documentCommentRevisions[documentId] ?? 0) + 1;
    },
    refreshDocumentCommentsAfterReconnect(): void {
      this.documentCommentsRecoveryRevision++;
    },
    getDocumentPresence(documentId: EntityId): DocumentPresenceParticipant[] {
      return this.documentPresence[documentId] ?? [];
    },
    applyDocumentPresenceSnapshot(message: DocumentPresenceSnapshotMessage): void {
      this.documentPresence = {};
      for (const document of message.documents ?? []) {
        this.applyDocumentPresenceChanged(document);
      }
    },
    applyDocumentPresenceChanged(message: DocumentPresenceChangedMessage): void {
      const documentId = toEntityId(message.documentId);
      const currentUserId = useSessionStore().profile?.id;
      const participants = (message.participants ?? [])
        .map(normalizePresenceParticipant)
        .filter(participant => participant.userId !== currentUserId);
      if (participants.length === 0) {
        delete this.documentPresence[documentId];
        return;
      }

      this.documentPresence[documentId] = participants;
    },
    applyDocumentContentSaved(message: DocumentContentSavedMessage): void {
      const documentId = toEntityId(message.documentId);
      const contentVersion = toContentVersion(message.contentVersion);
      this.updateDocumentContentVersion(documentId, contentVersion);
      const document = this.documents[documentId];
      if (document == null
        || this.dirtyDocuments[documentId]
        || this.saveConflicts[documentId]
        || (this.pendingDraftWrites[documentId] ?? 0) > 0) {
        return;
      }

      void this.loadDocument(documentId, true);
    },
    async applyWorkspaceTreeChanged(message: WorkspaceTreeChangedMessage): Promise<void> {
      if (this.workspace == null) {
        return;
      }

      try {
        switch (message.entityKind) {
          case 'Project':
            this.applyProjectTreeChange(message);
            break;
          case 'Folder':
            this.applyFolderTreeChange(message);
            break;
          case 'Document':
            this.applyDocumentTreeChange(message);
            break;
          default:
            throw new Error('Workspace tree entity kind is invalid.');
        }
      } catch (error) {
        await this.reloadWorkspace();
      }
    },
    applyProjectTreeChange(message: WorkspaceTreeChangedMessage): void {
      this.ensureWorkspaceLoaded();
      const projectId = toEntityId(message.entityId);
      if (message.kind === 'Deleted') {
        const project = removeProject(this.workspace!.projects, projectId);
        if (project != null) {
          for (const documentId of collectProjectDocumentIds(project)) {
            this.handleDeletedDocument(documentId);
          }
        }

        return;
      }

      if (message.kind === 'Created' || message.kind === 'Renamed') {
        const name = requireTreeName(message.name);
        let project = this.findProject(projectId);
        if (project == null) {
          project = {
            id: projectId,
            name,
            canEdit: message.canEdit === true,
            isExpanded: false,
            folders: [],
            documents: []
          };
          this.workspace!.projects.push(project);
        } else {
          project.name = name;
          if (message.canEdit != null) {
            project.canEdit = message.canEdit;
          }
        }

        sortProjects(this.workspace!.projects);
        this.syncEditorStateWithWorkspace();
        void subscribeWorkspaceRealtime();
        return;
      }

      throw new Error('Workspace project tree change is invalid.');
    },
    applyFolderTreeChange(message: WorkspaceTreeChangedMessage): void {
      this.ensureWorkspaceLoaded();
      const folderId = toEntityId(message.entityId);
      if (message.kind === 'Deleted') {
        const folder = removeFolderFromProjects(this.workspace!.projects, folderId);
        if (folder != null) {
          for (const documentId of collectFolderDocumentIds(folder)) {
            this.handleDeletedDocument(documentId);
          }
        }

        return;
      }

      const existing = removeFolderFromProjects(this.workspace!.projects, folderId);
      if (existing == null && message.kind !== 'Created') {
        throw new Error('Changed folder was not found in workspace tree.');
      }

      const folder: WorkspaceFolderTree = existing ?? {
        id: folderId,
        projectId: toEntityId(message.projectId),
        parentFolderId: toNullableEntityId(message.parentFolderId),
        name: requireTreeName(message.name),
        isExpanded: false,
        folders: [],
        documents: []
      };
      folder.projectId = toEntityId(message.projectId);
      folder.parentFolderId = toNullableEntityId(message.parentFolderId);
      folder.name = requireTreeName(message.name);
      insertFolder(this.workspace!.projects, folder);
      this.syncEditorStateWithWorkspace();
    },
    applyDocumentTreeChange(message: WorkspaceTreeChangedMessage): void {
      this.ensureWorkspaceLoaded();
      const documentId = toEntityId(message.entityId);
      if (message.kind === 'Deleted') {
        removeDocumentFromProjects(this.workspace!.projects, documentId);
        this.handleDeletedDocument(documentId);
        return;
      }

      const existing = removeDocumentFromProjects(this.workspace!.projects, documentId);
      if (existing == null && message.kind !== 'Created') {
        throw new Error('Changed document was not found in workspace tree.');
      }

      const document: WorkspaceDocumentTree = existing ?? {
        id: documentId,
        projectId: toEntityId(message.projectId),
        folderId: toNullableEntityId(message.parentFolderId),
        title: requireTreeName(message.title),
        contentVersion: toContentVersion(message.contentVersion)
      };
      document.projectId = toEntityId(message.projectId);
      document.folderId = toNullableEntityId(message.parentFolderId);
      document.title = requireTreeName(message.title);
      document.contentVersion = toContentVersion(message.contentVersion);
      insertDocument(this.workspace!.projects, document);

      const loadedDocument = this.documents[documentId];
      if (loadedDocument != null) {
        loadedDocument.title = document.title;
        loadedDocument.projectId = document.projectId;
      }

      this.syncEditorStateWithWorkspace();
    },
    handleDeletedDocument(documentId: EntityId): void {
      delete this.documentPresence[documentId];
      if (this.dirtyDocuments[documentId]) {
        this.clearSaveTimer(documentId);
        this.setSaveState(documentId, 'blocked', 'Document was deleted.');
        return;
      }

      this.removeDocumentFromEditor(documentId);
      this.clearLocalDocumentState(documentId);
      void this.syncRealtimeOpenDocuments();
    },
    removeDocumentFromEditor(documentId: EntityId): void {
      let removed = false;
      for (const pane of this.editorState.panes) {
        const tabs = pane.tabs.filter(tab => tab.documentId !== documentId);
        if (tabs.length === pane.tabs.length) {
          continue;
        }

        removed = true;
        pane.tabs = tabs;
        if (pane.activeDocumentId === documentId) {
          pane.activeDocumentId = pane.tabs[0]?.documentId ?? null;
        }
      }

      if (!removed) {
        return;
      }

      if (this.getPane(this.editorState.activePaneIndex).tabs.length === 0
        && this.getPane(secondaryPaneIndex).tabs.length > 0) {
        this.editorState.activePaneIndex = secondaryPaneIndex;
      }

      this.persistEditorState();
    },
    clearLocalDocumentState(documentId: EntityId): void {
      delete this.documents[documentId];
      delete this.drafts[documentId];
      delete this.draftBaseContentVersions[documentId];
      delete this.dirtyDocuments[documentId];
      delete this.saveStates[documentId];
      delete this.retryAttempts[documentId];
      delete this.pendingDraftWrites[documentId];
      delete this.saveConflicts[documentId];
    },
    async createProject(name: string): Promise<void> {
      const project = await CommandsService.commandCreateProjectCommand({ requestBody: { name } });
      this.addProjectToWorkspace(project);
    },
    async createFolder(projectId: EntityId, parentFolderId: EntityId | null, name: string): Promise<void> {
      const folder = await CommandsService.commandCreateFolderCommand({
        requestBody: { projectId, parentFolderId, name }
      });
      this.addFolderToWorkspace(folder);
      await this.expandParentNode(projectId, parentFolderId);
    },
    async createDocument(projectId: EntityId, folderId: EntityId | null, title: string): Promise<EntityId> {
      const document = await CommandsService.commandCreateDocumentCommand({
        requestBody: { projectId, folderId, title }
      });
      const documentId = toEntityId(document.id);
      this.addDocumentToWorkspace(document);
      await this.applyLoadedDocument(document.document);
      await this.openDocument(documentId);
      await this.expandParentNode(projectId, folderId);
      return documentId;
    },
    async renameFolder(folderId: EntityId, name: string): Promise<void> {
      const snapshot = this.createMutationSnapshot();
      try {
        const folder = removeFolderFromProjects(this.requireWorkspace().projects, folderId);
        if (folder == null) {
          throw new Error('Folder was not found in the workspace.');
        }

        folder.name = name;
        insertFolder(this.requireWorkspace().projects, folder);
        await CommandsService.commandRenameFolderCommand({ requestBody: { folderId, name } });
      } catch (error) {
        this.restoreMutationSnapshot(snapshot);
        throw error;
      }
    },
    async renameDocument(documentId: EntityId, title: string): Promise<void> {
      const snapshot = this.createMutationSnapshot();
      try {
        const document = removeDocumentFromProjects(this.requireWorkspace().projects, documentId);
        if (document == null) {
          throw new Error('Document was not found in the workspace.');
        }

        document.title = title;
        insertDocument(this.requireWorkspace().projects, document);
        const loadedDocument = this.documents[documentId];
        if (loadedDocument != null) {
          loadedDocument.title = title;
        }

        this.syncEditorStateWithWorkspace();
        await CommandsService.commandRenameDocumentCommand({ requestBody: { documentId, title } });
      } catch (error) {
        this.restoreMutationSnapshot(snapshot);
        throw error;
      }
    },
    async deleteFolder(folderId: EntityId): Promise<void> {
      const snapshot = this.createMutationSnapshot();
      try {
        const folder = removeFolderFromProjects(this.requireWorkspace().projects, folderId);
        if (folder == null) {
          throw new Error('Folder was not found in the workspace.');
        }

        for (const documentId of collectFolderDocumentIds(folder)) {
          this.handleDeletedDocument(documentId);
        }

        await CommandsService.commandDeleteFolderCommand({ requestBody: { folderId } });
      } catch (error) {
        this.restoreMutationSnapshot(snapshot);
        throw error;
      }
    },
    async deleteDocument(documentId: EntityId): Promise<void> {
      const snapshot = this.createMutationSnapshot();
      try {
        const document = removeDocumentFromProjects(this.requireWorkspace().projects, documentId);
        if (document == null) {
          throw new Error('Document was not found in the workspace.');
        }

        this.handleDeletedDocument(documentId);
        await CommandsService.commandDeleteDocumentCommand({ requestBody: { documentId } });
      } catch (error) {
        this.restoreMutationSnapshot(snapshot);
        throw error;
      }
    },
    async moveFolder(folderId: EntityId, parentFolderId: EntityId | null): Promise<void> {
      const snapshot = this.createMutationSnapshot();
      try {
        const folder = removeFolderFromProjects(this.requireWorkspace().projects, folderId);
        if (folder == null) {
          throw new Error('Folder was not found in the workspace.');
        }

        folder.parentFolderId = parentFolderId;
        insertFolder(this.requireWorkspace().projects, folder);
        await CommandsService.commandMoveFolderCommand({ requestBody: { folderId, parentFolderId } });
      } catch (error) {
        this.restoreMutationSnapshot(snapshot);
        throw error;
      }
    },
    async moveDocument(documentId: EntityId, folderId: EntityId | null): Promise<void> {
      const snapshot = this.createMutationSnapshot();
      try {
        const document = removeDocumentFromProjects(this.requireWorkspace().projects, documentId);
        if (document == null) {
          throw new Error('Document was not found in the workspace.');
        }

        document.folderId = folderId;
        insertDocument(this.requireWorkspace().projects, document);
        this.syncEditorStateWithWorkspace();
        await CommandsService.commandMoveDocumentCommand({ requestBody: { documentId, folderId } });
      } catch (error) {
        this.restoreMutationSnapshot(snapshot);
        throw error;
      }
    },
    async setProjectExpanded(projectId: EntityId, isExpanded: boolean): Promise<void> {
      const project = this.findProject(projectId);
      if (!project) {
        throw new Error('Project was not found in the workspace.');
      }

      if (project.isExpanded === isExpanded) {
        return;
      }

      project.isExpanded = isExpanded;
      this.persistTreeExpansionState();
      this.error = null;
    },
    async setFolderExpanded(folderId: EntityId, isExpanded: boolean): Promise<void> {
      const folder = this.findFolder(folderId);
      if (!folder) {
        throw new Error('Folder was not found in the workspace.');
      }

      if (folder.isExpanded === isExpanded) {
        return;
      }

      folder.isExpanded = isExpanded;
      this.persistTreeExpansionState();
      this.error = null;
    },
    async revealFolder(projectId: EntityId, folderId: EntityId): Promise<void> {
      if (!this.workspace) {
        await this.loadWorkspace();
      }

      let project = this.findProject(projectId);
      let folder = this.findFolder(folderId);
      if (!project || !folder) {
        await this.loadWorkspace();
        project = this.findProject(projectId);
        folder = this.findFolder(folderId);
      }

      if (!project || !folder || folder.projectId !== projectId) {
        throw new Error('Folder was not found in the workspace.');
      }

      project.isExpanded = true;
      folder.isExpanded = true;
      const visited = new Set<EntityId>([folder.id]);
      let parentFolderId = folder.parentFolderId;
      while (parentFolderId != null) {
        if (visited.has(parentFolderId)) {
          throw new Error('Workspace folder tree contains a cycle.');
        }

        visited.add(parentFolderId);
        const parent = this.findFolder(parentFolderId);
        if (!parent || parent.projectId !== projectId) {
          throw new Error('Folder parent was not found in the workspace.');
        }

        parent.isExpanded = true;
        parentFolderId = parent.parentFolderId;
      }

      this.persistTreeExpansionState();
      this.error = null;
    },
    setWorkspaceTreeExpanded(isExpanded: boolean): void {
      for (const project of this.requireWorkspace().projects) {
        applyProjectTreeExpansion(project, isExpanded);
      }

      this.persistTreeExpansionState();
      this.error = null;
    },
    setProjectTreeExpanded(projectId: EntityId, isExpanded: boolean): void {
      const project = this.findProject(projectId);
      if (!project) {
        throw new Error('Project was not found in the workspace.');
      }

      applyProjectTreeExpansion(project, isExpanded);
      this.persistTreeExpansionState();
      this.error = null;
    },
    setFolderTreeExpanded(folderId: EntityId, isExpanded: boolean): void {
      const folder = this.findFolder(folderId);
      if (!folder) {
        throw new Error('Folder was not found in the workspace.');
      }

      applyFolderTreeExpansion(folder, isExpanded);
      this.persistTreeExpansionState();
      this.error = null;
    },
    findProject(projectId: EntityId): WorkspaceProjectTree | null {
      return this.workspace?.projects.find(project => project.id === projectId) ?? null;
    },
    findFolder(folderId: EntityId): WorkspaceFolderTree | null {
      for (const project of this.workspace?.projects ?? []) {
        const folder = findFolder(project.folders, folderId);
        if (folder) {
          return folder;
        }
      }

      return null;
    },
    hasDocumentInWorkspace(documentId: EntityId): boolean {
      return this.workspace?.projects.some(project => findDocumentInProject(project, documentId) != null) === true;
    },
    restoreEditorState(): void {
      this.editorState = normalizeEditorState(getEditorTabsState(this.requireUserId()) ?? createEmptyEditorState());
    },
    syncEditorStateWithWorkspace(): void {
      const previousOpenDocumentIds = new Set(getOpenDocumentIds(this.editorState));
      const panes = createEmptyEditorState().panes;
      const existingPanes = new Map(this.editorState.panes.map(pane => [pane.paneIndex, pane]));

      for (const pane of panes) {
        const existingPane = existingPanes.get(pane.paneIndex);
        if (existingPane == null) {
          continue;
        }

        pane.tabs = existingPane.tabs
          .map(tab => this.findDocumentTabMetadata(tab.documentId, pane.paneIndex, tab.sortOrder))
          .filter((tab): tab is EditorTabState => tab != null);
        sortTabs(pane.tabs);
        pane.activeDocumentId = existingPane.activeDocumentId != null
          && pane.tabs.some(tab => tab.documentId === existingPane.activeDocumentId)
          ? existingPane.activeDocumentId
          : pane.tabs[0]?.documentId ?? null;
      }

      this.editorState = {
        activePaneIndex: normalizePaneIndex(this.editorState.activePaneIndex),
        panes
      };

      if (this.getPane(this.editorState.activePaneIndex).tabs.length === 0 && this.getPane(secondaryPaneIndex).tabs.length > 0) {
        this.editorState.activePaneIndex = secondaryPaneIndex;
      }

      this.persistEditorState();
      this.purgeClosedDocuments(previousOpenDocumentIds);
      void this.syncRealtimeOpenDocuments();
    },
    applyTreeExpansionState(): void {
      const workspace = this.workspace;
      if (workspace == null) {
        throw new Error('Workspace is not loaded.');
      }

      const storedState = getWorkspaceTreeExpansionState(this.requireUserId()) ?? {
        projectIds: [],
        folderIds: []
      };
      const expandedProjectIds = new Set(storedState.projectIds);
      const expandedFolderIds = new Set(storedState.folderIds);
      const validProjectIds: EntityId[] = [];
      const validFolderIds: EntityId[] = [];

      for (const project of workspace.projects) {
        project.isExpanded = expandedProjectIds.has(project.id);
        if (project.isExpanded) {
          validProjectIds.push(project.id);
        }

        applyFolderExpansionState(project.folders, expandedFolderIds, validFolderIds);
      }

      setWorkspaceTreeExpansionState(this.requireUserId(), {
        projectIds: validProjectIds,
        folderIds: validFolderIds
      });
    },
    persistEditorState(): void {
      setEditorTabsState(this.requireUserId(), this.editorState);
    },
    persistTreeExpansionState(): void {
      const workspace = this.workspace;
      if (workspace == null) {
        throw new Error('Workspace is not loaded.');
      }

      const state: WorkspaceTreeExpansionState = {
        projectIds: workspace.projects
          .filter(project => project.isExpanded)
          .map(project => project.id),
        folderIds: workspace.projects.flatMap(project => getExpandedFolderIds(project.folders))
      };
      setWorkspaceTreeExpansionState(this.requireUserId(), state);
    },
    getPane(paneIndex: number): EditorPaneState {
      const normalizedPaneIndex = normalizePaneIndex(paneIndex);
      const pane = this.editorState.panes.find(candidate => candidate.paneIndex === normalizedPaneIndex);
      if (pane == null) {
        throw new Error('Editor pane was not found.');
      }

      return pane;
    },
    findPaneContainingDocument(documentId: EntityId): EditorPaneState | null {
      return this.editorState.panes.find(pane => pane.tabs.some(tab => tab.documentId === documentId)) ?? null;
    },
    getDocumentTabMetadata(documentId: EntityId): Omit<EditorTabState, 'paneIndex' | 'sortOrder'> {
      const metadata = this.findDocumentTabMetadata(documentId, primaryPaneIndex, 0);
      if (metadata == null) {
        throw new Error('Document was not found in the workspace.');
      }

      return {
        documentId: metadata.documentId,
        title: metadata.title,
        projectName: metadata.projectName,
        contentVersion: metadata.contentVersion
      };
    },
    findDocumentTabMetadata(documentId: EntityId, paneIndex: number, sortOrder: number): EditorTabState | null {
      for (const project of this.workspace?.projects ?? []) {
        const document = findDocumentInProject(project, documentId);
        if (document != null) {
          return {
            documentId,
            paneIndex,
            title: document.title,
            projectName: project.name,
            contentVersion: document.contentVersion,
            sortOrder
          };
        }
      }

      return null;
    },
    updateDocumentContentVersion(documentId: EntityId, contentVersion: number): void {
      for (const project of this.workspace?.projects ?? []) {
        const document = findDocumentInProject(project, documentId);
        if (document != null) {
          document.contentVersion = contentVersion;
        }
      }

      let updatedTabs = false;
      for (const pane of this.editorState.panes) {
        for (const tab of pane.tabs) {
          if (tab.documentId !== documentId) {
            continue;
          }

          tab.contentVersion = contentVersion;
          updatedTabs = true;
        }
      }

      if (updatedTabs) {
        this.persistEditorState();
      }
    },
    purgeClosedDocuments(previousOpenDocumentIds: Set<EntityId> | null = null): void {
      const openDocumentIds = new Set(getOpenDocumentIds(this.editorState));
      const candidateIds = previousOpenDocumentIds ?? new Set(Object.keys(this.documents));

      for (const documentId of candidateIds) {
        if (openDocumentIds.has(documentId)) {
          continue;
        }

        delete this.documents[documentId];
        delete this.drafts[documentId];
        delete this.draftBaseContentVersions[documentId];
        delete this.dirtyDocuments[documentId];
        delete this.saveStates[documentId];
        delete this.retryAttempts[documentId];
        delete this.pendingDraftWrites[documentId];
        delete this.saveConflicts[documentId];
      }
    },
    requireUserId(): EntityId {
      const userId = useSessionStore().profile?.id;
      if (userId == null) {
        throw new Error('Current user was not loaded.');
      }

      return userId;
    },
    ensureWorkspaceLoaded(): void {
      if (!this.workspace) {
        throw new Error('Workspace is not loaded.');
      }
    },
    requireWorkspace(): WorkspaceResponse {
      if (!this.workspace) {
        throw new Error('Workspace is not loaded.');
      }

      return this.workspace;
    },
    addProjectToWorkspace(project: CreateProjectCommandResult): void {
      const workspace = this.requireWorkspace();
      const projectId = toEntityId(project.id);
      removeProject(workspace.projects, projectId);
      workspace.projects.push({
        id: projectId,
        name: project.name,
        canEdit: true,
        isExpanded: false,
        folders: [],
        documents: []
      });
      sortProjects(workspace.projects);
    },
    addFolderToWorkspace(folder: CreateFolderCommandResult): void {
      if (!this.workspace) {
        throw new Error('Workspace is not loaded.');
      }

      const item: WorkspaceFolderTree = {
        id: toEntityId(folder.id),
        projectId: toEntityId(folder.projectId),
        parentFolderId: toNullableEntityId(folder.parentFolderId),
        name: folder.name,
        isExpanded: false,
        folders: [],
        documents: []
      };

      removeFolderFromProjects(this.workspace.projects, item.id);
      if (folder.parentFolderId != null) {
        const parent = this.findFolder(folder.parentFolderId);
        if (!parent) {
          throw new Error('Created folder parent was not found in the workspace.');
        }

        parent.folders.push(item);
        sortFolders(parent.folders);
        return;
      }

      const project = this.workspace.projects.find(candidate => candidate.id === folder.projectId);
      if (!project) {
        throw new Error('Created folder project was not found in the workspace.');
      }

      project.folders.push(item);
      sortFolders(project.folders);
    },
    addDocumentToWorkspace(document: CreateDocumentCommandResult): void {
      if (!this.workspace) {
        throw new Error('Workspace is not loaded.');
      }

      const item: WorkspaceDocumentTree = {
        id: toEntityId(document.id),
        projectId: toEntityId(document.projectId),
        folderId: toNullableEntityId(document.folderId),
        title: document.title,
        contentVersion: toContentVersion(document.contentVersion)
      };

      removeDocumentFromProjects(this.workspace.projects, item.id);
      if (document.folderId != null) {
        const folder = this.findFolder(document.folderId);
        if (!folder) {
          throw new Error('Created document folder was not found in the workspace.');
        }

        folder.documents.push(item);
        sortDocuments(folder.documents);
        return;
      }

      const project = this.workspace.projects.find(candidate => candidate.id === document.projectId);
      if (!project) {
        throw new Error('Created document project was not found in the workspace.');
      }

      project.documents.push(item);
      sortDocuments(project.documents);
    },
    async expandParentNode(projectId: EntityId, folderId: EntityId | null): Promise<void> {
      if (folderId != null) {
        await this.setFolderExpanded(folderId, true);
        return;
      }

      await this.setProjectExpanded(projectId, true);
    },
    createMutationSnapshot(): WorkspaceMutationSnapshot {
      return {
        workspace: cloneSnapshotValue(this.workspace),
        editorState: cloneSnapshotValue(this.editorState),
        documents: cloneSnapshotValue(this.documents),
        drafts: cloneSnapshotValue(this.drafts),
        draftBaseContentVersions: cloneSnapshotValue(this.draftBaseContentVersions),
        dirtyDocuments: cloneSnapshotValue(this.dirtyDocuments),
        saveStates: cloneSnapshotValue(this.saveStates),
        retryAttempts: cloneSnapshotValue(this.retryAttempts),
        pendingDraftWrites: cloneSnapshotValue(this.pendingDraftWrites),
        saveConflicts: cloneSnapshotValue(this.saveConflicts),
        documentPresence: cloneSnapshotValue(this.documentPresence)
      };
    },
    restoreMutationSnapshot(snapshot: WorkspaceMutationSnapshot): void {
      this.workspace = snapshot.workspace;
      this.editorState = snapshot.editorState;
      this.documents = snapshot.documents;
      this.drafts = snapshot.drafts;
      this.draftBaseContentVersions = snapshot.draftBaseContentVersions;
      this.dirtyDocuments = snapshot.dirtyDocuments;
      this.saveStates = snapshot.saveStates;
      this.retryAttempts = snapshot.retryAttempts;
      this.pendingDraftWrites = snapshot.pendingDraftWrites;
      this.saveConflicts = snapshot.saveConflicts;
      this.documentPresence = snapshot.documentPresence;
      this.persistEditorState();
    },
    scheduleRetry(documentId: EntityId): void {
      this.clearSaveTimer(documentId);
      const attempt = this.retryAttempts[documentId] ?? 0;
      const delay = retryDelayMs[Math.min(attempt, retryDelayMs.length - 1)];
      this.retryAttempts[documentId] = attempt + 1;
      this.setSaveState(documentId, 'retrying', null);
      this.saveTimers[documentId] = window.setTimeout(() => {
        void this.saveDocument(documentId);
      }, delay);
    },
    async registerSaveConflict(documentId: EntityId, serverDocument: GetDocumentQueryResponse): Promise<void> {
      this.clearSaveTimer(documentId);
      const serverContentVersion = toContentVersion(serverDocument.contentVersion);
      this.documents[documentId] = serverDocument;
      this.updateDocumentContentVersion(documentId, serverContentVersion);
      if (this.getDraft(documentId) === serverDocument.contentMarkdown) {
        this.drafts[documentId] = serverDocument.contentMarkdown;
        this.draftBaseContentVersions[documentId] = serverContentVersion;
        delete this.dirtyDocuments[documentId];
        delete this.retryAttempts[documentId];
        delete this.saveStates[documentId];
        delete this.saveConflicts[documentId];
        await this.deleteDraftFromStorage(documentId);
        return;
      }

      this.saveConflicts[documentId] = {
        documentId,
        title: serverDocument.title,
        serverContentMarkdown: serverDocument.contentMarkdown,
        serverContentVersion,
        serverSavedAt: serverDocument.savedAt,
        serverLastModifiedByUserName: serverDocument.lastModifiedByUserName
      };
      this.setSaveState(documentId, 'conflict', null);
      this.error = null;
    },
    setSaveState(documentId: EntityId, kind: DocumentSaveStateKind, message: string | null): void {
      this.saveStates[documentId] = { kind, message };
    },
    queueDraftWrite(documentId: EntityId, contentMarkdown: string, baseContentVersion: number): void {
      const userId = this.requireUserId();
      const record: DocumentDraftRecord = {
        userId,
        documentId,
        contentMarkdown,
        baseContentVersion,
        updatedAt: Date.now()
      };
      void this.runDraftStorageOperation(documentId, () => setDocumentDraft(record));
    },
    queueDraftDelete(documentId: EntityId): void {
      void this.deleteDraftFromStorage(documentId);
    },
    async deleteDraftFromStorage(documentId: EntityId): Promise<void> {
      const userId = this.requireUserId();
      await this.runDraftStorageOperation(documentId, () => deleteDocumentDraft(userId, documentId));
    },
    async runDraftStorageOperation(documentId: EntityId, operation: () => Promise<void>): Promise<void> {
      const queueKey = `${this.requireUserId()}:${documentId}`;
      const previous = draftWriteQueue[queueKey] ?? Promise.resolve();
      this.beginDraftWrite(documentId);
      const current = previous.catch(() => undefined).then(operation);
      draftWriteQueue[queueKey] = current;
      try {
        await current;
      } catch (error) {
        const message = getErrorMessage(error, 'Unable to update local document draft.');
        this.error = message;
        if (this.dirtyDocuments[documentId]) {
          this.setSaveState(documentId, 'blocked', message);
        }
      } finally {
        if (draftWriteQueue[queueKey] === current) {
          delete draftWriteQueue[queueKey];
        }

        this.endDraftWrite(documentId);
      }
    },
    beginDraftWrite(documentId: EntityId): void {
      this.pendingDraftWrites[documentId] = (this.pendingDraftWrites[documentId] ?? 0) + 1;
    },
    endDraftWrite(documentId: EntityId): void {
      const remaining = (this.pendingDraftWrites[documentId] ?? 1) - 1;
      if (remaining > 0) {
        this.pendingDraftWrites[documentId] = remaining;
        return;
      }

      delete this.pendingDraftWrites[documentId];
    },
    hasUnsavedDocumentChanges(): boolean {
      return Object.values(this.dirtyDocuments).some(Boolean)
        || Object.values(this.pendingDraftWrites).some(count => count > 0);
    },
    registerUnloadSave(): void {
      if (this.unloadRegistered) {
        return;
      }

      window.addEventListener('beforeunload', event => {
        if (this.hasUnsavedDocumentChanges()) {
          event.preventDefault();
          event.returnValue = '';
        }

        for (const key of Object.keys(this.dirtyDocuments)) {
          const documentId = key;
          const document = this.documents[documentId];
          if (!document) {
            continue;
          }

          const draft = this.getDraft(documentId);
          if (draft === document.contentMarkdown) {
            continue;
          }

          void saveDocumentWithKeepalive(
            {
              documentId,
              expectedContentVersion: this.draftBaseContentVersions[documentId] ?? document.contentVersion,
              contentMarkdown: draft
            }
          );
        }
      });
      this.unloadRegistered = true;
    }
  }
});

function normalizeWorkspaceResponse(response: GetWorkspaceQueryResponse): WorkspaceResponse {
  return {
    projects: (response.projects ?? []).map(normalizeProject)
  };
}

function cloneSnapshotValue<T>(value: T): T {
  return structuredClone(unwrapSnapshotValue(value)) as T;
}

function unwrapSnapshotValue(value: unknown): unknown {
  const raw = toRaw(value);
  if (raw == null || typeof raw !== 'object' || raw instanceof Date) {
    return raw;
  }

  if (Array.isArray(raw)) {
    return raw.map(unwrapSnapshotValue);
  }

  return Object.fromEntries(Object.entries(raw).map(([key, item]) => [key, unwrapSnapshotValue(item)]));
}

function normalizeProject(project: ProjectTreeResponse): WorkspaceProjectTree {
  return {
    id: toEntityId(project.id),
    name: project.name,
    canEdit: project.canEdit === true,
    isExpanded: false,
    folders: (project.folders ?? []).map(normalizeFolder),
    documents: (project.documents ?? []).map(normalizeDocument)
  };
}

function normalizeFolder(folder: FolderTreeResponse): WorkspaceFolderTree {
  return {
    id: toEntityId(folder.id),
    projectId: toEntityId(folder.projectId),
    parentFolderId: toNullableEntityId(folder.parentFolderId),
    name: folder.name,
    isExpanded: false,
    folders: (folder.folders ?? []).map(normalizeFolder),
    documents: (folder.documents ?? []).map(normalizeDocument)
  };
}

function normalizeDocument(document: DocumentTreeResponse): WorkspaceDocumentTree {
  return {
    id: toEntityId(document.id),
    projectId: toEntityId(document.projectId),
    folderId: toNullableEntityId(document.folderId),
    title: document.title,
    contentVersion: toContentVersion(document.contentVersion)
  };
}

function toEntityId(value: unknown): EntityId {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return String(value);
  }

  throw new Error('Invalid entity id received from API.');
}

function toNullableEntityId(value: unknown): EntityId | null {
  return value == null ? null : toEntityId(value);
}

function toContentVersion(value: unknown): number {
  const contentVersion = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(contentVersion) || contentVersion < 0) {
    throw new Error('Invalid content version received from API.');
  }

  return contentVersion;
}

function classifySaveFailure(error: unknown): 'transient' | 'conflict' | 'fatal' {
  if (!(error instanceof ApiError)) {
    return 'transient';
  }

  if (error.status === 409) {
    return 'conflict';
  }

  if ([429, 500, 502, 503, 504].includes(error.status)) {
    return 'transient';
  }

  return 'fatal';
}

function normalizePresenceParticipant(value: {
  userId: unknown;
  userName: string;
  mode: DocumentPresenceMode;
  lastChangedAt?: unknown;
}): DocumentPresenceParticipant {
  if (value.mode !== 'Viewing' && value.mode !== 'Editing') {
    throw new Error('Document presence mode is invalid.');
  }

  return {
    userId: toEntityId(value.userId),
    userName: value.userName,
    mode: value.mode,
    lastChangedAt: value.lastChangedAt == null ? null : String(value.lastChangedAt)
  };
}

function requireTreeName(value: unknown): string {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  throw new Error('Workspace tree name is invalid.');
}

function findFolder(folders: WorkspaceFolderTree[], folderId: EntityId): WorkspaceFolderTree | null {
  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }

    const child = findFolder(folder.folders, folderId);
    if (child) {
      return child;
    }
  }

  return null;
}

function applyProjectTreeExpansion(project: WorkspaceProjectTree, isExpanded: boolean): void {
  project.isExpanded = isExpanded;
  for (const folder of project.folders) {
    applyFolderTreeExpansion(folder, isExpanded);
  }
}

function applyFolderTreeExpansion(folder: WorkspaceFolderTree, isExpanded: boolean): void {
  folder.isExpanded = isExpanded;
  for (const child of folder.folders) {
    applyFolderTreeExpansion(child, isExpanded);
  }
}

function removeProject(projects: WorkspaceProjectTree[], projectId: EntityId): WorkspaceProjectTree | null {
  const index = projects.findIndex(project => project.id === projectId);
  if (index < 0) {
    return null;
  }

  return projects.splice(index, 1)[0];
}

function removeFolderFromProjects(projects: WorkspaceProjectTree[], folderId: EntityId): WorkspaceFolderTree | null {
  for (const project of projects) {
    const folder = removeFolderFromFolders(project.folders, folderId);
    if (folder != null) {
      return folder;
    }
  }

  return null;
}

function removeFolderFromFolders(folders: WorkspaceFolderTree[], folderId: EntityId): WorkspaceFolderTree | null {
  const index = folders.findIndex(folder => folder.id === folderId);
  if (index >= 0) {
    return folders.splice(index, 1)[0];
  }

  for (const folder of folders) {
    const child = removeFolderFromFolders(folder.folders, folderId);
    if (child != null) {
      return child;
    }
  }

  return null;
}

function insertFolder(projects: WorkspaceProjectTree[], folder: WorkspaceFolderTree): void {
  if (folder.parentFolderId != null) {
    const parent = projects
      .flatMap(project => project.folders)
      .map(root => findFolder([root], folder.parentFolderId!))
      .find(candidate => candidate != null);
    if (parent == null) {
      throw new Error('Target folder parent was not found in workspace tree.');
    }

    parent.folders.push(folder);
    sortFolders(parent.folders);
    return;
  }

  const project = projects.find(candidate => candidate.id === folder.projectId);
  if (project == null) {
    throw new Error('Target folder project was not found in workspace tree.');
  }

  project.folders.push(folder);
  sortFolders(project.folders);
}

function removeDocumentFromProjects(projects: WorkspaceProjectTree[], documentId: EntityId): WorkspaceDocumentTree | null {
  for (const project of projects) {
    const projectDocumentIndex = project.documents.findIndex(document => document.id === documentId);
    if (projectDocumentIndex >= 0) {
      return project.documents.splice(projectDocumentIndex, 1)[0];
    }

    const folderDocument = removeDocumentFromFolders(project.folders, documentId);
    if (folderDocument != null) {
      return folderDocument;
    }
  }

  return null;
}

function removeDocumentFromFolders(folders: WorkspaceFolderTree[], documentId: EntityId): WorkspaceDocumentTree | null {
  for (const folder of folders) {
    const documentIndex = folder.documents.findIndex(document => document.id === documentId);
    if (documentIndex >= 0) {
      return folder.documents.splice(documentIndex, 1)[0];
    }

    const childDocument = removeDocumentFromFolders(folder.folders, documentId);
    if (childDocument != null) {
      return childDocument;
    }
  }

  return null;
}

function insertDocument(projects: WorkspaceProjectTree[], document: WorkspaceDocumentTree): void {
  if (document.folderId != null) {
    const folder = projects
      .flatMap(project => project.folders)
      .map(root => findFolder([root], document.folderId!))
      .find(candidate => candidate != null);
    if (folder == null) {
      throw new Error('Target document folder was not found in workspace tree.');
    }

    folder.documents.push(document);
    sortDocuments(folder.documents);
    return;
  }

  const project = projects.find(candidate => candidate.id === document.projectId);
  if (project == null) {
    throw new Error('Target document project was not found in workspace tree.');
  }

  project.documents.push(document);
  sortDocuments(project.documents);
}

function collectProjectDocumentIds(project: WorkspaceProjectTree): EntityId[] {
  return [
    ...project.documents.map(document => document.id),
    ...project.folders.flatMap(collectFolderDocumentIds)
  ];
}

function collectFolderDocumentIds(folder: WorkspaceFolderTree): EntityId[] {
  return [
    ...folder.documents.map(document => document.id),
    ...folder.folders.flatMap(collectFolderDocumentIds)
  ];
}

function findDocumentInProject(project: WorkspaceProjectTree, documentId: EntityId): WorkspaceDocumentTree | null {
  const projectDocument = project.documents.find(document => document.id === documentId);
  if (projectDocument != null) {
    return projectDocument;
  }

  return findDocumentInFolders(project.folders, documentId);
}

function findDocumentInFolders(folders: WorkspaceFolderTree[], documentId: EntityId): WorkspaceDocumentTree | null {
  for (const folder of folders) {
    const document = folder.documents.find(candidate => candidate.id === documentId);
    if (document != null) {
      return document;
    }

    const childDocument = findDocumentInFolders(folder.folders, documentId);
    if (childDocument != null) {
      return childDocument;
    }
  }

  return null;
}

function applyFolderExpansionState(
  folders: WorkspaceFolderTree[],
  expandedFolderIds: Set<EntityId>,
  validFolderIds: EntityId[]
): void {
  for (const folder of folders) {
    folder.isExpanded = expandedFolderIds.has(folder.id);
    if (folder.isExpanded) {
      validFolderIds.push(folder.id);
    }

    applyFolderExpansionState(folder.folders, expandedFolderIds, validFolderIds);
  }
}

function getExpandedFolderIds(folders: WorkspaceFolderTree[]): EntityId[] {
  return folders.flatMap(folder => [
    ...(folder.isExpanded ? [folder.id] : []),
    ...getExpandedFolderIds(folder.folders)
  ]);
}

function createEmptyEditorState(): EditorTabsState {
  return {
    activePaneIndex: primaryPaneIndex,
    panes: [
      { paneIndex: primaryPaneIndex, activeDocumentId: null, tabs: [] },
      { paneIndex: secondaryPaneIndex, activeDocumentId: null, tabs: [] }
    ]
  };
}

function normalizeEditorState(state: EditorTabsState): EditorTabsState {
  const normalized = createEmptyEditorState();
  const panes = new Map(state.panes.map(pane => [normalizePaneIndex(pane.paneIndex), pane]));

  for (const pane of normalized.panes) {
    const storedPane = panes.get(pane.paneIndex);
    if (storedPane == null) {
      continue;
    }

    pane.tabs = storedPane.tabs
      .map(tab => ({
        ...tab,
        paneIndex: pane.paneIndex,
        sortOrder: Math.max(0, Math.trunc(tab.sortOrder))
      }))
      .sort(compareTabs);
    pane.activeDocumentId = storedPane.activeDocumentId != null
      && pane.tabs.some(tab => tab.documentId === storedPane.activeDocumentId)
      ? storedPane.activeDocumentId
      : pane.tabs[0]?.documentId ?? null;
  }

  normalized.activePaneIndex = normalizePaneIndex(state.activePaneIndex);
  return normalized;
}

function normalizePaneIndex(paneIndex: number): number {
  return paneIndex === secondaryPaneIndex ? secondaryPaneIndex : primaryPaneIndex;
}

function getNextSortOrder(pane: EditorPaneState): number {
  return Math.max(-1, ...pane.tabs.map(tab => tab.sortOrder)) + 1;
}

function sortTabs(tabs: EditorTabState[]): void {
  tabs.sort(compareTabs);
}

function compareTabs(left: EditorTabState, right: EditorTabState): number {
  return left.sortOrder - right.sortOrder;
}

function getOpenDocumentIds(state: EditorTabsState): EntityId[] {
  return state.panes.flatMap(pane => pane.tabs.map(tab => tab.documentId));
}

function sortFolders(folders: WorkspaceFolderTree[]): void {
  folders.sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
}

function sortDocuments(documents: WorkspaceDocumentTree[]): void {
  documents.sort((left, right) => left.title.localeCompare(right.title, undefined, { sensitivity: 'base' }));
}

function sortProjects(projects: WorkspaceProjectTree[]): void {
  projects.sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
}

function formatDateTime(instant: unknown): string {
  return new Date(String(instant)).toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function paneClass(pane: EditorPaneState, activePaneIndex: number, paneCount: number): string {
  return paneCount > 1 && pane.paneIndex === activePaneIndex ? 'editor-pane active' : 'editor-pane';
}
